import { ReactNode } from 'react';
import { TailwindRoot } from 'v4/shared/TailwindRoot';
import { z, ZodError, ZodType } from 'zod';

const CAUSE_DATA_LIMIT = 4000;

function summariseData(data: unknown): string | undefined {
  try {
    const json = JSON.stringify(data);
    if (json == null) {
      return undefined;
    }
    return json.length > CAUSE_DATA_LIMIT
      ? `${json.slice(0, CAUSE_DATA_LIMIT)}… (truncated from ${json.length})`
      : json;
  } catch {
    return undefined;
  }
}

export class SchemaError extends Error {
  innerError: ZodError;
  data: unknown;
  /**
   * Data that error handler could use if the error issuer could not validate the data.
   * This is useful when we optionally want to provide data to the error handler when validation fails.
   * Usually, the caller validates data and processes it, but when validation fails, this is the way to provide unsecure mapped data to the error handler.
   */
  fallbackData: unknown;
  constructor(innerError: ZodError, data: unknown, fallbackData?: unknown) {
    super(innerError.message, {
      cause: { issues: innerError.issues, data: summariseData(data) }
    });
    this.name = 'SchemaError';
    this.innerError = innerError;
    this.data = data;
    this.fallbackData = fallbackData;
  }
}

export function parseWithDecoratedError<T extends ZodType<any>>(
  schema: T,
  data: any
): z.infer<T> {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof ZodError) {
      throw new SchemaError(error, data);
    }
    throw error;
  }
}

export interface ZodErrorWrapperProps<T> {
  error: T;
  children: (error: T) => ReactNode;
}

export function SchemaErrorWrapper<T>(props: ZodErrorWrapperProps<T>) {
  const { error, children } = props;

  if (!(error instanceof SchemaError)) {
    return children(error);
  }
  return (
    <TailwindRoot>
      <div className="p-3 flex gap-4 items-center justify-center">
        <div>Schema error</div>
        <button
          onClick={() => {
            console.error(
              'Schema validation error',
              error.data,
              error.innerError
            );
          }}
          className="font-bold transition-all rounded p-3 bg-red-400 hover:bg-red-700 focus-visible:ring text-white text-left text-md"
        >
          log schema error
        </button>
      </div>
    </TailwindRoot>
  );
}
